import { useState } from 'react';
import {
  makeStyles, tokens, Button, Input, Field, Text,
  MessageBar, MessageBarBody, MessageBarTitle,
  Dialog, DialogSurface, DialogBody, DialogTitle,
  DialogContent, DialogActions, DialogTrigger, Link,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useApi } from '../api/context';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM, minWidth: '420px' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  url: { wordBreak: 'break-all', fontSize: tokens.fontSizeBase200 },
});

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Registers a customer tenant.
 *
 * Until this existed the only way in was a PowerShell script, which made the
 * Run assessment button on the customer page a feature you could not reach.
 *
 * The home region is not asked for. It comes from the stamp, so a customer
 * cannot be placed in a region this deployment does not serve.
 */
export function AddCustomer({ onAdded }: { onAdded: () => void }) {
  const styles = useStyles();
  const api = useApi();

  const [open, setOpen] = useState(false);
  const [entraTenantId, setEntraTenantId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ tenantId: string; name: string } | null>(null);
  const [consentUrl, setConsentUrl] = useState<string | null>(null);

  const validGuid = GUID.test(entraTenantId.trim());
  const canSave = validGuid && displayName.trim().length > 0 && !busy;

  const reset = () => {
    setEntraTenantId(''); setDisplayName(''); setPrimaryDomain('');
    setError(null); setCreated(null); setConsentUrl(null); setBusy(false);
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const t = await api.createTenant({
        entraTenantId: entraTenantId.trim(),
        displayName: displayName.trim(),
        primaryDomain: primaryDomain.trim() || null,
      });
      setCreated({ tenantId: t.tenantId, name: t.displayName });
      onAdded();
      try {
        setConsentUrl((await api.getConsentUrl(t.tenantId)).url);
      } catch { /* the customer exists; the link is a convenience */ }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { setOpen(d.open); if (!d.open) reset(); }}>
      <DialogTrigger disableButtonEnhancement>
        <Button appearance="primary" size="small" icon={<AddRegular />}>Add customer</Button>
      </DialogTrigger>

      <DialogSurface>
        <DialogBody>
          <DialogTitle>{created ? `${created.name} added` : 'Add a customer'}</DialogTitle>

          <DialogContent>
            <div className={styles.body}>
              {created ? (
                <>
                  <MessageBar intent="success">
                    <MessageBarBody>
                      <MessageBarTitle>Registered</MessageBarTitle>
                      Nothing can be collected until a Global Administrator in their tenant
                      grants admin consent.
                    </MessageBarBody>
                  </MessageBar>

                  {consentUrl && (
                    <Field label="Admin consent link">
                      <Text className={styles.url}>
                        <Link href={consentUrl} target="_blank" rel="noreferrer">{consentUrl}</Link>
                      </Text>
                    </Field>
                  )}

                  <Text className={styles.muted}>
                    Consent creates a service principal in the customer's own directory and
                    grants read only access. No credential is shared with us, and they can
                    revoke it themselves at any time from Enterprise applications.
                  </Text>
                </>
              ) : (
                <>
                  <Field
                    label="Entra tenant ID"
                    required
                    validationState={entraTenantId && !validGuid ? 'error' : 'none'}
                    {...(entraTenantId && !validGuid
                      ? { validationMessage: 'This should be the tenant GUID, not a domain name.' }
                      : {})}
                    hint="The customer's directory ID. Also what stops the same tenant being registered twice."
                  >
                    <Input
                      value={entraTenantId}
                      placeholder="00000000-0000-0000-0000-000000000000"
                      onChange={(_, d) => setEntraTenantId(d.value)}
                    />
                  </Field>

                  <Field label="Display name" required>
                    <Input value={displayName} onChange={(_, d) => setDisplayName(d.value)}
                           placeholder="Contoso Engineering" />
                  </Field>

                  <Field label="Primary domain" hint="Optional, and only used to make the list readable.">
                    <Input value={primaryDomain} onChange={(_, d) => setPrimaryDomain(d.value)}
                           placeholder="contoso.com" />
                  </Field>

                  <Text className={styles.muted}>
                    The data region is taken from this deployment rather than chosen here, so a
                    customer cannot be placed in a region it does not serve.
                  </Text>

                  {error && (
                    <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
                  )}
                </>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{created ? 'Close' : 'Cancel'}</Button>
            </DialogTrigger>
            {!created && (
              <Button appearance="primary" disabled={!canSave} onClick={() => void save()}>
                {busy ? 'Adding' : 'Add'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
